import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Campanhas() {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(user?.empresa_id || "");
  const [lista, setLista] = useState([]);
  const [indice, setIndice] = useState(null);
  const [novo, setNovo] = useState(false);
  const [form, setForm] = useState({ nome: "", estado: "", cidade: "", bairro: "", tipo_servico: "montagem",
    valor_incentivo: 0, modo_incentivo: "fixo", data_inicio: "", data_fim: "", observacoes: "" });
  const [salvando, setSalvando] = useState(false);
  const [sugestaoCidade, setSugestaoCidade] = useState({ estado: "", cidade: "" });
  const [sugestaoResult, setSugestaoResult] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") api.get("/empresas").then(r => setEmpresas(r.data));
  }, [user]);

  const load = async () => {
    if (user?.role === "montadora") {
      const r = await api.get("/montadora/campanhas");
      setLista(r.data);
      return;
    }
    if (!empresaId) return;
    const [r, i] = await Promise.all([
      api.get(`/empresas/${empresaId}/campanhas`),
      api.get(`/empresas/${empresaId}/indice-atratividade`),
    ]);
    setLista(r.data); setIndice(i.data);
  };

  useEffect(() => { load(); }, [empresaId, user?.role]);

  const ehMontadora = user?.role === "montadora";

  const salvar = async () => {
    if (!form.nome || !form.data_inicio || !form.data_fim) { toast.error("Preencha nome e datas"); return; }
    setSalvando(true);
    try {
      await api.post(`/empresas/${empresaId}/campanhas`, form);
      toast.success("Campanha criada · montadores da região notificados");
      setNovo(false);
      setForm({ nome: "", estado: "", cidade: "", bairro: "", tipo_servico: "montagem",
        valor_incentivo: 0, modo_incentivo: "fixo", data_inicio: "", data_fim: "", observacoes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  const checarSugestao = async () => {
    if (!sugestaoCidade.estado || !sugestaoCidade.cidade) { toast.error("Informe UF e cidade"); return; }
    try {
      const r = await api.get(`/empresas/${empresaId}/campanhas/sugestao`,
        { params: sugestaoCidade });
      setSugestaoResult(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap">{ehMontadora ? "Comercial · Montadora" : "Comercial · Empresa"}</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Campanhas de Incentivo</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {ehMontadora
              ? "Campanhas vigentes das empresas parceiras — bônus válidos para os montadores da sua rede."
              : "Aumenta atratividade operacional dos atendimentos. Não interfere na distribuição (que continua por região/capacidade/SLA/score)."}
          </p>
        </div>
        {!ehMontadora && (
          <Button onClick={() => setNovo(true)} disabled={!empresaId} className="btn-sharp brand-bg text-white"
            data-testid="cp-novo">+ Nova campanha</Button>
        )}
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <Select value={empresaId} onValueChange={setEmpresaId}>
            <SelectTrigger className="max-w-md" data-testid="cp-empresa"><SelectValue placeholder="Selecione empresa" /></SelectTrigger>
            <SelectContent>{empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      {/* Índice de Atratividade */}
      {!ehMontadora && indice && (
        <div className="border border-border bg-white p-5 mb-6" data-testid="cp-indice">
          <div className="flex items-center gap-6">
            <div>
              <div className="label-cap">Índice de Atratividade Operacional</div>
              <div className={`font-heading font-extrabold text-5xl ${indice.indice >= 70 ? "text-[#00A859]" : indice.indice >= 40 ? "text-[#FFCC00]" : "text-[#E60000]"}`}>
                {indice.indice}
              </div>
              <div className="text-[10px] text-muted-foreground">de 100</div>
            </div>
            <div className="flex-1 grid grid-cols-4 gap-3 text-xs">
              <div><div className="label-cap text-[10px]">Pgto em dia</div><div className="font-mono font-bold mt-1">{indice.componentes.pagamento_em_dia_pct}%</div></div>
              <div><div className="label-cap text-[10px]">Taxa aceite</div><div className="font-mono font-bold mt-1">{indice.componentes.taxa_aceite_pct}%</div></div>
              <div><div className="label-cap text-[10px]">Campanhas ativas</div><div className="font-mono font-bold mt-1">{indice.componentes.campanhas_ativas}</div></div>
              <div><div className="label-cap text-[10px]">Tempo médio pgto</div><div className="font-mono font-bold mt-1">{indice.componentes.tempo_medio_pagamento_dias}d</div></div>
            </div>
          </div>
        </div>
      )}

      {/* Verificador de sugestão */}
      {!ehMontadora && (
      <div className="border border-border bg-white p-5 mb-6">
        <div className="label-cap mb-2">Sugestão automática (ocupação ≥70% → recomenda campanha)</div>
        <div className="flex gap-2 items-end">
          <div className="flex-1 max-w-[100px]">
            <Label className="text-xs">UF</Label>
            <Input value={sugestaoCidade.estado} maxLength={2}
              onChange={(e) => setSugestaoCidade({ ...sugestaoCidade, estado: e.target.value.toUpperCase() })}
              data-testid="sg-uf" />
          </div>
          <div className="flex-1">
            <Label className="text-xs">Cidade</Label>
            <Input value={sugestaoCidade.cidade}
              onChange={(e) => setSugestaoCidade({ ...sugestaoCidade, cidade: e.target.value })}
              data-testid="sg-cidade" />
          </div>
          <Button onClick={checarSugestao} variant="outline" className="btn-sharp" data-testid="sg-checar">Verificar</Button>
        </div>
        {sugestaoResult && (
          <div className={`mt-3 border-l-4 p-3 text-xs ${sugestaoResult.sugerir ? "border-[#FFCC00] bg-[#FFCC00]/10" : "border-[#00A859] bg-[#00A859]/5"}`} data-testid="sg-result">
            <strong>{sugestaoResult.sugerir ? "🎯 Recomendado abrir campanha" : "✓ Não é necessário"}:</strong> {sugestaoResult.motivo}
            {sugestaoResult.ocupacao_pct != null && <div className="mt-1">Ocupação: {sugestaoResult.ocupacao_pct}% ({sugestaoResult.qtd_no_mes}/{sugestaoResult.volumetria_mensal} atend/mês)</div>}
          </div>
        )}
      </div>
      )}

      <div className="border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F4F5] border-b border-border">
            <tr>
              <th className="text-left p-3 label-cap">Nome</th>
              <th className="text-left p-3 label-cap">Região</th>
              <th className="text-left p-3 label-cap">Serviço</th>
              <th className="text-left p-3 label-cap">Incentivo</th>
              <th className="text-left p-3 label-cap">Vigência</th>
              <th className="text-left p-3 label-cap">Aceitos</th>
              <th className="text-left p-3 label-cap">Status</th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">
              {ehMontadora ? "Nenhuma campanha vigente das empresas parceiras" : "Nenhuma campanha"}</td></tr>}
            {lista.map(c => (
              <tr key={c.id} className="border-b border-border last:border-0" data-testid={`cp-${c.id}`}>
                <td className="p-3 font-medium">{c.nome}</td>
                <td className="p-3 text-xs">{c.cidade}/{c.estado}{c.bairro ? ` · ${c.bairro}` : ""}</td>
                <td className="p-3 text-xs uppercase">{c.tipo_servico || "—"}</td>
                <td className="p-3 font-mono">{c.modo_incentivo === "fixo" ? `R$ ${c.valor_incentivo.toFixed(2)}` : `${c.valor_incentivo}%`}</td>
                <td className="p-3 text-xs">{c.data_inicio} → {c.data_fim}</td>
                <td className="p-3 font-mono">{c.qtd_atendimentos_aceitos || 0}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase ${c.ativo ? "bg-[#00A859] text-white" : "bg-secondary"}`}>
                    {c.ativo ? "Ativa" : "Inativa"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={novo} onOpenChange={setNovo}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nova campanha de incentivo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-cap">Nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex: Bônus Black Friday SP" data-testid="cp-nome" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">UF</Label>
                <Input value={form.estado} maxLength={2}
                  onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase() })}
                  data-testid="cp-uf" /></div>
              <div className="col-span-2"><Label className="text-xs">Cidade</Label>
                <Input value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })}
                  data-testid="cp-cidade" /></div>
            </div>
            <div>
              <Label className="text-xs">Bairro (opcional)</Label>
              <Input value={form.bairro} onChange={(e) => setForm({ ...form, bairro: e.target.value })} data-testid="cp-bairro" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Modo</Label>
                <Select value={form.modo_incentivo} onValueChange={(v) => setForm({ ...form, modo_incentivo: v })}>
                  <SelectTrigger data-testid="cp-modo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixo">R$ fixo</SelectItem>
                    <SelectItem value="percentual">%</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Valor</Label>
                <Input type="number" step="0.01" value={form.valor_incentivo}
                  onChange={(e) => setForm({ ...form, valor_incentivo: parseFloat(e.target.value) || 0 })}
                  data-testid="cp-valor" /></div>
              <div><Label className="text-xs">Serviço</Label>
                <Input value={form.tipo_servico} onChange={(e) => setForm({ ...form, tipo_servico: e.target.value })}
                  data-testid="cp-servico" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Início</Label>
                <Input type="date" value={form.data_inicio}
                  onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} data-testid="cp-inicio" /></div>
              <div><Label className="text-xs">Fim</Label>
                <Input type="date" value={form.data_fim}
                  onChange={(e) => setForm({ ...form, data_fim: e.target.value })} data-testid="cp-fim" /></div>
            </div>
            <div>
              <Label className="text-xs">Observações</Label>
              <Textarea rows={2} value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="cp-obs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovo(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={salvando} className="brand-bg text-white" data-testid="cp-salvar">
              {salvando ? "Salvando…" : "Criar campanha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
